import React, { useEffect, useState, useCallback, useMemo, useContext } from "react";
import { Card, CardHeader, Icon } from "@ui5/webcomponents-react";
import { ColumnChartWithTrend } from "@ui5/webcomponents-react-charts";
import httpRequest from "../httpRequest";
import UserContext from "../context/user-context"; // Import UserContext

const MyExpensesCard = () => {
  const [expensesData, setExpensesData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Access the UserContext
  const userCtx = useContext(UserContext);
  const { employeeCode } = userCtx; // Get the employee code from context

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const expensesResp = await httpRequest.get(
        `${process.env.REACT_APP_BACKEND_URL}/get-expenses?exclude_fields=expenses&employee_code=${employeeCode}`
      );
      setExpensesData(expensesResp.data.expenses);
    } catch (error) {
      setError("Error fetching data. Please try again later.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [employeeCode]); // Add employeeCode as a dependency

  useEffect(() => {
    if (employeeCode) {
      fetchData(); // Fetch data only when employeeCode is available
    }
  }, [employeeCode, fetchData]);

  const mappedExpenses = useMemo(() => {
    return expensesData.map((expense) => {
      const total_base_amount = parseFloat(expense.total_base_amount).toFixed(2);
      const base_currency = expense.base_currency;
      const status = expense.status; // (Pending, Initiated and Approved)

      return {
        ...expense,
        total_base_amount,
        base_currency,
        status,
      };
    });
  }, [expensesData]);

  const chartData = useMemo(() => {
    return mappedExpenses.reduce((acc, expense) => {
      const index = acc.findIndex((item) => item.status === expense.status);
      if (index !== -1) {
        acc[index].count += 1;
      } else {
        acc.push({
          status: expense.status,
          count: 1,
          total_base_amount: parseFloat(expense.total_base_amount),
        });
      }
      return acc;
    }, []);
  }, [mappedExpenses]);

  return (
    <Card
      header={<CardHeader avatar={<Icon name="my-view" />} subtitleText="Total Current Expenses" titleText="My Expenses" />}
    >
      {loading ? (
        <div style={{ textAlign: "center" }}>Loading...</div>
      ) : error ? (
        <div style={{ color: "#e26300", textAlign: "center" }}>{error}</div>
      ) : (
        <div style={{ height: 425 }}>
          <ColumnChartWithTrend
            dataset={chartData}
            dimensions={[
              {
                accessor: "status",
              },
            ]}
            measures={[
              {
                accessor: "count",
                label: "Count",
                type: "bar",
              },
              {
                accessor: "total_base_amount",
                label: "Amount",
                type: "line",
              },
            ]}
            style={{
              height: "400px",
            }}
          />
        </div>
      )}
    </Card>
  );
};

export default MyExpensesCard;
